import { GeneralLedgerTransaction } from '../../models/common/GeneralLedgerTransaction';
import { IGNORE_ERROR_HANDLING, IS_SPINNER_ACTIVE } from '../../../../util/interceptors';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlService } from 'src/environments/urlService';
import { ExcelGeneralLedgerDTO } from '../../models/DTOs/gl-upload/ExcelGeneralLedgerDTO';
import { FinancialStatementDTO } from '../../models/DTOs/common/FinancialStatementDTO';
import { FinancialStatementItemDTO } from '../../models/DTOs/common/FinancialStatementItemDTO';
import { GeneralLedgerTransactionDTO } from '../../models/DTOs/common/GeneralLedgerTransactionDTO';
import { FinancialStatementAccountDTO } from '../../models/DTOs/common/FinancialStatementAccountDTO';
import { FsStatusPeriodDTO } from '../../utils/common/utilityClasses';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FinancialStatementBackendService {

  private readonly url;

  constructor(
    private httpClient: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'financial-statement';
  }


  deleteFs(fsUuid: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.url + '/fs',
      { params: { fsUuid },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true });
  }

  /**
   * Combines all sent chunks on the backend by session UUID after all chunks have been successfully sent.
   * Note: fsService methods have their own error handling -> set(IGNORE_ERROR_HANDLING, true)
   */
  saveChunkedGL(
    sessionUUID: string, totalNumberOfChunks: number, ownershipEntityUuid: string, ownershipType: string, date: Date,
  ): Observable<void> {
    const period: string = date.toString();
    return this.httpClient.post<void>(this.url + '/gl-chunks', sessionUUID, {
      params: { ownershipEntityUuid, ownershipType, totalNumberOfChunks, period },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false).set(IGNORE_ERROR_HANDLING, true),
      withCredentials: true,
    });
  }

  saveGL(excelData: ExcelGeneralLedgerDTO[], ownershipEntityUuid: string, ownershipType: string, date: Date): Observable<void> {
    const period: string = date.toString();
    return this.httpClient.post<void>(this.url + '/gl', excelData, {
      params: { ownershipEntityUuid, ownershipType, period },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,
    });
  }

  getGl(ownershipEntityUuid: string, ownershipType: string, date: Date, withManualEntries: boolean):
  Observable<GeneralLedgerTransaction[]> {
    const period: string = date.toString();
    return this.httpClient.get<GeneralLedgerTransaction[]>(this.url + '/gl', {
      params: { ownershipEntityUuid, ownershipType, period, withManualEntries },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getGlForEntityWithoutPeriod(ownershipEntityUuid: string, ownershipType: string): Observable<GeneralLedgerTransaction[]> {
    return this.httpClient.get<GeneralLedgerTransaction[]>(this.url + '/gl', {
      params: { ownershipEntityUuid, ownershipType },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  saveManualEntry(manualEntry: GeneralLedgerTransactionDTO, fsUuid: string): Observable<GeneralLedgerTransactionDTO[]> {
    return this.httpClient.post<GeneralLedgerTransactionDTO[]>(this.url + '/manual-entry', manualEntry,
      { params: { fsUuid },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true });
  }

  saveManualEntries(manualEntries: GeneralLedgerTransactionDTO[], fsUuid: string): Observable<GeneralLedgerTransactionDTO[]> {
    return this.httpClient.post<GeneralLedgerTransactionDTO[]>(this.url + '/manual-entries', manualEntries, {
      params: { fsUuid },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,

    });
  }

  deleteManualEntries(manualEntryIds: number[]): Observable<void> {
    return this.httpClient.delete<void>(this.url + '/manual-entries/' + manualEntryIds.toString(), {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,

    });
  }


  convertManualEntries(entryId: number, oldJournalEntryCode: string): Observable<GeneralLedgerTransactionDTO[]> {
    return this.httpClient.put<GeneralLedgerTransactionDTO[]>(this.url + '/manual-entry', null, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      params: { entryId, oldJournalEntryCode },
      withCredentials: true,
    });
  }

  getFinancialStatement(
    ownershipEntityUuid: string, ownershipType: string, date: Date, showSpinner: boolean,
  ): Observable<FinancialStatementDTO> {
    const period: string = date.toString();
    return this.httpClient.get<FinancialStatementDTO>(this.url, {
      params: { ownershipEntityUuid, ownershipType, period },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, showSpinner),
      withCredentials: true,
    });
  }

  getAllFinancialStatementsStatusForEntity(ownershipEntityUuid: string, ownershipType: string): Observable<FsStatusPeriodDTO[]> {
    return this.httpClient.get<FsStatusPeriodDTO[]>(this.url + '/all-status', {
      params: { ownershipEntityUuid, ownershipType },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,
    });
  }

  changeFsStatus(fsUuid: string, isCompleted: boolean): Observable<void> {
    if (!fsUuid) {
      return;
    }
    return this.httpClient.put<void>(this.url + '/change-status', null, {
      params: { fsUuid, isCompleted },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,
    });
  }

  clearFsStructure(fsUuid: string): Observable<void> {
    return this.httpClient.put<void>(this.url + '/clear-structure', null, {
      params: { fsUuid },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,
    });
  }

  copyFsStructure(fsUuid: string, date: Date): Observable<void> {
    const periodForCopy: string = date.toString();
    return this.httpClient.put<void>(this.url + '/copy-structure', null, {
      params: { fsUuid, periodForCopy },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  updateItem(item: FinancialStatementItemDTO): Observable<void> {
    return this.httpClient.put<void>(this.url + '/item', item, { withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false) });
  }

  saveItem(financialStatementItemDTO: FinancialStatementItemDTO, fsUuid: string): Observable<FinancialStatementItemDTO> {
    return this.httpClient.post<FinancialStatementItemDTO>(
      this.url + '/item', financialStatementItemDTO,
      { params: { fsUuid },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true },
    );
  }

  deleteItem(itemId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.url + '/item',
      { context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        params: { itemId },
        withCredentials: true },
    );
  }

  saveManualAccount(manualAccount: FinancialStatementAccountDTO, fsUuid: string): Observable<FinancialStatementAccountDTO> {
    return this.httpClient.post<FinancialStatementAccountDTO>(this.url + '/manual-account', manualAccount,
      { params: { fsUuid },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false) },
    );
  }

  deleteManualAccounts(accountIds: number[]): Observable<number[]> {
    return this.httpClient.delete<number[]>(this.url + '/manual-accounts/' + accountIds.toString(), {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      withCredentials: true,
    });
  }
}
